import { useState } from 'react'
import { useDocumentHead } from '@/hooks/use-document-head'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent } from '@/components/ui/card'
import { useReportingData } from '@/hooks/useReportingData'
import { ReportingHeader } from '@/components/reporting/ReportingHeader'
import { CallsTab } from '@/components/reporting/CallsTab'
import { SmsTab } from '@/components/reporting/SmsTab'
import { ActionsTab } from '@/components/reporting/ActionsTab'
import {
  formatTimeSaved,
  formatCallDuration,
  escapeCsvField,
} from '@/utils/reporting-formatters'
import { REPORTING_LABELS } from '@/utils/reporting-constants'
import { ExportType } from '@/components/reporting/ReportingHeader'
import { reportingApiClient } from '@/lib/api/reporting.client'
import { useAppAuth } from '@/contexts/app-auth.context'
import { getReportingDateParams } from '@/utils/reporting-date-range'

export function ReportingPage() {
  const [activeTab, setActiveTab] = useState<string>('calls')
  const [chartType, setChartType] = useState<'line' | 'bar'>('line')
  const { isSuperAdmin, selectedCompanyId, currentCompany } = useAppAuth()

  const {
    callReport,
    smsReport,
    activityReport,
    actionMetrics,
    isFetching,
    error,
    dateRange,
    setDateRange,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate,
    appliedCustomDates,
    handleCustomDateRangeApply,
    handleCustomDateRangeCancel,
  } = useReportingData()

  useDocumentHead({
    title: 'Dashboard',
    description: 'View your call and SMS performance metrics',
  })

  const getDateRangeLabel = () => {
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      return `${customStartDate}_to_${customEndDate}`
    }
    return dateRange
  }

  const downloadCsv = (csvContent: string, filename: string) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleAggregatedExport = () => {
    if (!callReport && !smsReport) {
      return
    }

    const csvRows: string[] = []
    csvRows.push('Dashboard Reporting Export')

    if (callReport) {
      const outbound = callReport.outbound
      const callTime = (totalCallTime: number) => {
        const fmt = formatTimeSaved(totalCallTime)
        return `${fmt.value} ${fmt.label}`
      }

      csvRows.push(
        `Call Metrics,${REPORTING_LABELS.INBOUND_COLUMN},${REPORTING_LABELS.OUTBOUND_COLUMN}`,
      )
      csvRows.push(
        `${REPORTING_LABELS.CALLS},${callReport.handledByAI ?? 0},${outbound.handledByAI ?? 0}`,
      )
      csvRows.push(
        `${REPORTING_LABELS.NEEDS_HUMAN_HELP},${callReport.notResolvedCalls ?? 0},${outbound.notResolvedCalls ?? 0}`,
      )
      csvRows.push(
        `${REPORTING_LABELS.NOT_RESOLVED_PCT},${callReport.notResolvedPercentage ?? 0}%,${outbound.notResolvedPercentage ?? 0}%`,
      )
      csvRows.push(
        `${REPORTING_LABELS.TOTAL_PROCESSED_CALLS},${callReport.totalProcessedCalls ?? 0},${outbound.totalProcessedCalls ?? 0}`,
      )
      csvRows.push(
        `${REPORTING_LABELS.AI_TALK_TIME},${callTime(callReport.totalAiTalkTime ?? 0)},${callTime(outbound.totalAiTalkTime ?? 0)}`,
      )
      csvRows.push(
        `${REPORTING_LABELS.AVG_AI_TALK_TIME},${formatCallDuration(callReport.averageAiTalkTime ?? 0)},${formatCallDuration(outbound.averageAiTalkTime ?? 0)}`,
      )
      // Not tracked per-direction: sourced from workflow action metrics, which have no direction concept.
      csvRows.push(
        `${REPORTING_LABELS.CALLS_TRANSFERRED},${callReport.callsTransferred ?? actionMetrics?.byType.PHONE_CALL ?? 0},`,
      )
      csvRows.push('')
    }

    if (smsReport) {
      csvRows.push('SMS Metrics,Value')
      csvRows.push(
        `${REPORTING_LABELS.TOTAL_MESSAGES_HANDLED},${smsReport.handledByAI ?? 0}`,
      )
      csvRows.push(
        `${REPORTING_LABELS.TOTAL_PROCESSED_MESSAGES},${(smsReport.resolvedSMS ?? 0) + (smsReport.notResolvedSMS ?? 0)}`,
      )
      csvRows.push(
        `${REPORTING_LABELS.SMS_NEEDS_HUMAN_HELP},${smsReport.notResolvedSMS ?? 0}`,
      )
      csvRows.push(
        `${REPORTING_LABELS.SMS_NOT_RESOLVED_PCT},${smsReport.notResolvedPercentage ?? 0}%`,
      )
      csvRows.push('')
    }

    downloadCsv(
      csvRows.join('\n'),
      `dashboard-report-${getDateRangeLabel()}-${new Date().toISOString().split('T')[0]}.csv`,
    )
  }

  const getDateParams = () => {
    return getReportingDateParams(
      dateRange,
      currentCompany?.timezoneName,
      appliedCustomDates,
    )
  }

  const handleIndividualExport = async () => {
    const params = getDateParams()

    const response =
      isSuperAdmin && selectedCompanyId
        ? await reportingApiClient.getAdminIndividualCallReport({
            ...params,
            companyId: selectedCompanyId,
          })
        : await reportingApiClient.getIndividualCallReport(params)

    if (!response?.records?.length) {
      return
    }

    const headers = [
      REPORTING_LABELS.DATE,
      REPORTING_LABELS.DIRECTION,
      REPORTING_LABELS.FROM,
      REPORTING_LABELS.TO,
      REPORTING_LABELS.AI_TALK_TIME_S,
      REPORTING_LABELS.RESOLVED,
      REPORTING_LABELS.HANGUP,
      REPORTING_LABELS.SENTIMENT,
      REPORTING_LABELS.HANDOFF_RULE,
      REPORTING_LABELS.TRANSCRIPT,
    ]

    const csvRows = [headers.join(',')]

    for (const record of response.records) {
      const resolved =
        record.isResolved === true
          ? 'Yes'
          : record.isResolved === false
            ? 'No'
            : ''
      const direction = record.direction === 'OUTBOUND' ? 'Outbound' : 'Inbound'
      csvRows.push(
        [
          new Date(record.date).toISOString(),
          direction,
          escapeCsvField(record.from),
          escapeCsvField(record.to),
          record.aiLedDuration,
          resolved,
          record.hangup ? 'Yes' : 'No',
          escapeCsvField(record.sentiment ?? ''),
          escapeCsvField(record.handoffRule ?? ''),
          escapeCsvField(record.transcript ?? ''),
        ].join(','),
      )
    }

    downloadCsv(
      csvRows.join('\n'),
      `individual-calls-${getDateRangeLabel()}-${new Date().toISOString().split('T')[0]}.csv`,
    )
  }

  const handleExport = (type: ExportType) => {
    if (type === ExportType.AGGREGATED) {
      handleAggregatedExport()
    } else if (type === ExportType.INDIVIDUAL) {
      handleIndividualExport()
    }
  }

  return (
    <div className="bg-background w-full pt-16">
      <div className="flex flex-col gap-6 px-4 sm:px-6 pt-4 sm:pt-6 pb-20 sm:pb-20 w-full box-border">
        <ReportingHeader
          dateRange={dateRange}
          setDateRange={setDateRange}
          customStartDate={customStartDate}
          setCustomStartDate={setCustomStartDate}
          customEndDate={customEndDate}
          setCustomEndDate={setCustomEndDate}
          appliedCustomDates={appliedCustomDates}
          handleCustomDateRangeApply={handleCustomDateRangeApply}
          handleCustomDateRangeCancel={handleCustomDateRangeCancel}
          handleExport={handleExport}
          activeTab={activeTab}
        />

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-3 mb-4">
            <TabsTrigger value="calls">Calls</TabsTrigger>
            <TabsTrigger value="sms">SMS</TabsTrigger>
            <TabsTrigger value="actions">Actions</TabsTrigger>
          </TabsList>

          <TabsContent value="calls">
            <CallsTab
              callReport={callReport}
              activityReport={activityReport}
              actionMetrics={actionMetrics}
              dateRange={dateRange}
              appliedCustomDates={appliedCustomDates}
              chartType={chartType}
              setChartType={setChartType}
              isLoading={isFetching}
            />
          </TabsContent>

          <TabsContent value="sms">
            <SmsTab
              smsReport={smsReport}
              activityReport={activityReport}
              dateRange={dateRange}
              appliedCustomDates={appliedCustomDates}
              chartType={chartType}
              setChartType={setChartType}
              isLoading={isFetching}
            />
          </TabsContent>

          <TabsContent value="actions">
            <ActionsTab
              actionMetrics={actionMetrics}
              dateRange={dateRange}
              appliedCustomDates={appliedCustomDates}
              isLoading={isFetching}
            />
          </TabsContent>
        </Tabs>

        {error && (
          <Card className="border-error-border bg-error-subtle animate-in fade-in slide-in-from-top-4 duration-300">
            <CardContent className="pt-6">
              <p className="text-error">{error}</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
